import React from 'react';
import { motion } from 'framer-motion';
import { Card } from './ui/card';
import { AlertCircle, Shield, TrendingUp } from 'lucide-react';

const OpportunityMetrics = () => {
  const problems = [
    {
      icon: AlertCircle,
      title: 'Fragmented Supply',
      description: 'High-value collectibles scattered across dealers, private collections, and unverified marketplaces.'
    },
    {
      icon: Shield,
      title: 'Authentication Risk',
      description: 'No trusted provenance layer. Buyers hesitate. Sellers discount. Liquidity suffers.'
    },
    {
      icon: TrendingUp,
      title: 'Access Barrier',
      description: 'Capital allocators and serious buyers lack curated networks to deploy at scale.'
    }
  ];

  return (
    <>
      {/* Vision Section */}
      <section id="vision" className="py-16 lg:py-24 bg-secondary/30">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto text-center"
          >
            <p className="uppercase tracking-[0.22em] text-xs text-muted-foreground mb-6">
              Our Vision
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold mb-6">
              Build the Trust Layer First, Then the Platform
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              The $400B+ collectibles market lacks trusted infrastructure. We're not building another marketplace — we're building the trust layer that makes liquidity possible. Control the buyers, dealers, and capital allocators, and you control the market.{' '}
              <a 
                href="https://sovereigngroup.io" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-accent hover:underline font-medium"
              >
                Learn more about Sovereign →
              </a>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Opportunity Section */}
      <section id="opportunity" className="py-16 lg:py-24 relative">
        {/* Subtle texture background */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/4705933/pexels-photo-4705933.jpeg?auto=compress&cs=tinysrgb&w=1600)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />

        <div className="container relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <p className="text-5xl sm:text-6xl lg:text-7xl font-bold tabular-nums mb-4">
              $400B+
            </p>
            <p className="text-xl text-muted-foreground">
              Global collectibles market with no trust infrastructure
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {problems.map((problem, index) => {
              const Icon = problem.icon;
              return (
                <motion.div
                  key={problem.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="p-6 h-full hover:shadow-md transition-shadow">
                    <Icon className="h-8 w-8 mb-4 text-accent" />
                    <h3 className="text-xl font-semibold mb-3">{problem.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {problem.description}
                    </p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default OpportunityMetrics;
